import React from 'react';
import type {MessageDescriptor} from 'react-intl';
import {defineMessages} from 'react-intl';
import type {KeyValueFilter, Sorting} from '@igp/shared/api';
import {maxPageSize, SortOrder} from '@igp/shared/api';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {CurrencyColumn, DataGridColumn, TextColumn} from '@igp/shared/components/data-grid';
import type {Timestamp} from '@igp/shared/contract/pam';
import type {UseDownloadProps, UseDownloadResult} from '@igp/shared/redux/download';
import {useDownload} from '@igp/shared/redux/download';
import {useMessage} from '@igp/shared/redux/message';
import type {UseListViewEntityResult, UseViewEntityProps} from '@igp/shared/redux/view';
import {extendedViewCleanDelay} from '@igp/shared/redux/view';
import {formatTimestamp, isRangeInSameWeek} from '@igp/shared/utils';
import {EntityType} from '@models';
import {useViewInit} from '@redux';

import type {AgentLevelReportV2ServerFilterKeys} from '@features/agent-level-report-v2';

import {negativeCarryoverReportMappingKeys} from './mapper';
import type {
    AgentNegativeCarryoverReportExtended,
    NegativeCarryoverReportQueryFields,
    NegativeCarryoverReportSortKeys,
    NegativeCarryoverReportViewModel,
    NegativeCarryoverReportViewModelKeys,
    NegativeCarryoverWeeklySummaryViewModelKeys,
} from './types';
import {NegativeCarryoverReportServiceTypes} from './types';

const localized = defineMessages({
    weeklySummaryInvalidDateRangeError: {
        id: 'NegativeCarryoverReportHooks_weeklySummaryInvalidDateRange',
        defaultMessage:
            'Invalid selection: The selected date range spans multiple weeks. Please select a single "report_week_start" to proceed. Payments cannot be processed across multiple reporting weeks.',
    },
});

const localizedHeaders = defineMessages<NegativeCarryoverReportViewModelKeys>({
    id: {
        id: 'NegativeCarryoverReportHooks_id',
        defaultMessage: 'ID',
    },
    agentUid: {
        id: 'NegativeCarryoverReportHooks_agentUid',
        defaultMessage: 'Agent ID',
    },
    debtAfterPayment: {
        id: 'NegativeCarryoverReportHooks_debtAfterPayment',
        defaultMessage: 'Debt After Payment',
    },
    debtBeforePayment: {
        id: 'NegativeCarryoverReportHooks_debtBeforePayment',
        defaultMessage: 'Debt Before Payment',
    },
    debtTransaction: {
        id: 'NegativeCarryoverReportHooks_debtTransaction',
        defaultMessage: 'Debt Transaction',
    },
    totalNetShare: {
        id: 'NegativeCarryoverReportHooks_totalNetShare',
        defaultMessage: 'Weekly Revenue Share',
    },
    weekStartDateTs: {
        id: 'NegativeCarryoverReportHooks_weekStartDateTs',
        defaultMessage: 'Report Week Start',
    },
    weeklyActuallyPaid: {
        id: 'NegativeCarryoverReportHooks_weeklyActuallyPaid',
        defaultMessage: 'Weekly Actual Paid',
    },
});

const localizedWeeklySummaryHeaders = defineMessages<NegativeCarryoverWeeklySummaryViewModelKeys>({
    aal: {id: 'NegativeCarryoverReportHooks_aal', defaultMessage: 'AAL'},
    agent_uid: {id: 'NegativeCarryoverReportHooks_agent_uid', defaultMessage: 'UID'},
    comments: {id: 'NegativeCarryoverReportHooks_comments', defaultMessage: 'Comments'},
    direction: {id: 'NegativeCarryoverReportHooks_direction', defaultMessage: 'Direction'},
    reason_codes: {id: 'NegativeCarryoverReportHooks_reason_codes', defaultMessage: 'Reason Codes'},
    reference: {id: 'NegativeCarryoverReportHooks_reference', defaultMessage: 'Reference'},
    turnover_rate: {id: 'NegativeCarryoverReportHooks_turnover_rate', defaultMessage: 'Turnover Rate'},
    weekly_actually_paid: {id: 'NegativeCarryoverReportHooks_weekly_actually_paid', defaultMessage: 'Amount'},
});

export type UseNegativeCarryoverDownloadProps = Pick<
    UseDownloadProps<NegativeCarryoverReportViewModelKeys>,
    'filename' | 'filter' | 'keys' | 'mode' | 'total'
>;

export function useNegativeCarryoverDownload(props: UseNegativeCarryoverDownloadProps): UseDownloadResult {
    const {handleDownload, isProgress} = useDownload<NegativeCarryoverReportViewModelKeys>({
        ...props,
        strategySymbol:
            props.mode === 'client'
                ? NegativeCarryoverReportServiceTypes.DownloadClientStrategy
                : NegativeCarryoverReportServiceTypes.DownloadServerStrategy,
        headersLocalization: localizedHeaders,
    });
    return {handleDownload, isProgress};
}
export type UseNegativeCarryoverReportProps = Omit<
    UseViewEntityProps<EntityType, AgentLevelReportV2ServerFilterKeys, NegativeCarryoverReportViewModelKeys>,
    'schemaMapper' | 'entity' | 'cleanDelay' | 'defaultSorting'
> & {
    fields: NegativeCarryoverReportViewModelKeys[];
    saveFilters?: boolean;
};

type UseNegativeCarryoverReportResult = Omit<
    UseListViewEntityResult<NegativeCarryoverReportViewModel, NegativeCarryoverReportViewModelKeys>,
    'getSearchFilter' | 'forceViewUpdate' | 'viewEntity'
> & {
    lastUpdate?: Timestamp;
    filterString?: string;
};

export function useNegativeCarryoverReport({
    viewType,
    fields,
    saveFilters = true,
    defaultFilters,
    syncWithUrl,
    validateFilter,
    ...props
}: UseNegativeCarryoverReportProps): UseNegativeCarryoverReportResult {
    const mapper = useAutoMapper();
    const queryFields = fields.map(mapQueryField);
    const {
        items: serverItems,
        totalCount,
        getSearchFilter,
        viewEntity,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        isDataLoading,
    } = useViewInit<
        AgentNegativeCarryoverReportExtended,
        AgentLevelReportV2ServerFilterKeys,
        NegativeCarryoverReportQueryFields,
        NegativeCarryoverReportSortKeys
    >({
        viewType,
        entity: {
            entity: EntityType.NegaitveCarryoverReport,
            fields: queryFields,
        },
        cleanDelay: saveFilters ? extendedViewCleanDelay : 0,
        defaultFilters,
        defaultSorting: [
            {
                field: 'week_start_date_ts.seconds',
                sort: SortOrder.Desc,
            },
        ],
        syncWithUrl,
        validateFilter,
        ...props,
    });

    const items = serverItems.map(i =>
        mapper.tryMap<AgentNegativeCarryoverReportExtended, NegativeCarryoverReportViewModel>(
            i,
            negativeCarryoverReportMappingKeys.serverModel,
            negativeCarryoverReportMappingKeys.viewModel
        )
    );
    const reportItems = items.filter(i => !i?.model?.isGeneral);

    function mapQueryField(vmKey: NegativeCarryoverReportViewModelKeys): NegativeCarryoverReportQueryFields {
        return mapper.map<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportQueryFields>(
            vmKey,
            negativeCarryoverReportMappingKeys.viewModelKeys,
            negativeCarryoverReportMappingKeys.queryKeys
        );
    }

    function handleVmSortChange(sorting: Sorting<NegativeCarryoverReportViewModelKeys>[]) {
        handleSortChange(mapSorting(sorting));
    }

    function mapSorting(sorting: Sorting<NegativeCarryoverReportViewModelKeys>[]): Sorting<NegativeCarryoverReportSortKeys>[] {
        return sorting.map(s => ({
            field: mapper.map<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportSortKeys>(
                s.field,
                negativeCarryoverReportMappingKeys.viewModelKeys,
                negativeCarryoverReportMappingKeys.sortKeys
            ),
            sort: s.sort,
        }));
    }

    return {
        items: reportItems?.map(i => i?.model),
        rawItems: reportItems?.map(i => ({isValid: i?.isModelValid, raw: i?.rawModelString})),
        lastUpdate: items?.find(i => i?.model?.isGeneral)?.model?.lastUpdated,
        totalCount,
        searchFilter: getSearchFilter(i =>
            mapper.map<NegativeCarryoverReportSortKeys, NegativeCarryoverReportViewModelKeys>(
                i,
                negativeCarryoverReportMappingKeys.sortKeys,
                negativeCarryoverReportMappingKeys.viewModelKeys
            )
        ),
        filterString: viewEntity?.filter,
        isDataLoading,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange: handleVmSortChange,
    };
}

export function useDataGridNegativeCarryoverReportColumns(
    keys: NegativeCarryoverReportViewModelKeys[]
): DataGridColumn<NegativeCarryoverReportViewModel>[] {
    const currency = 'USD';
    const id: TextColumn<NegativeCarryoverReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.id,
        field: 'id',
        title: localizedHeaders.id,
        sortable: true,
    };
    const agentUid: TextColumn<NegativeCarryoverReportViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.agentUid,
        field: 'agentUid',
        title: localizedHeaders.agentUid,
        sortable: true,
    };
    const debtAfterPayment: CurrencyColumn<NegativeCarryoverReportViewModel> = {
        width: 200,
        type: 'currency',
        valueGetter: m => ({amount: m.debtAfterPayment, currency}),
        field: 'debtAfterPayment',
        title: localizedHeaders.debtAfterPayment,
        sortable: true,
    };
    const debtBeforePayment: CurrencyColumn<NegativeCarryoverReportViewModel> = {
        width: 200,
        type: 'currency',
        valueGetter: m => ({amount: m.debtBeforePayment, currency}),
        field: 'debtBeforePayment',
        title: localizedHeaders.debtBeforePayment,
        sortable: true,
    };
    const debtTransaction: CurrencyColumn<NegativeCarryoverReportViewModel> = {
        width: 150,
        type: 'currency',
        valueGetter: m => ({amount: m.debtTransaction, currency}),
        field: 'debtTransaction',
        title: localizedHeaders.debtTransaction,
        sortable: true,
    };
    const totalNetShare: CurrencyColumn<NegativeCarryoverReportViewModel> = {
        width: 200,
        type: 'currency',
        valueGetter: m => ({amount: m.totalNetShare, currency}),
        field: 'totalNetShare',
        title: localizedHeaders.totalNetShare,
        sortable: true,
    };
    const weekStartDateTs: TextColumn<NegativeCarryoverReportViewModel> = {
        width: 200,
        type: 'text',
        valueGetter: m => formatTimestamp(m.weekStartDateTs, 'date'),
        field: 'weekStartDateTs',
        title: localizedHeaders.weekStartDateTs,
        sortable: true,
    };
    const weeklyActuallyPaid: CurrencyColumn<NegativeCarryoverReportViewModel> = {
        width: 200,
        type: 'currency',
        valueGetter: m => ({amount: m.weeklyActuallyPaid, currency}),
        field: 'weeklyActuallyPaid',
        title: localizedHeaders.weeklyActuallyPaid,
        sortable: true,
    };

    const config: Record<NegativeCarryoverReportViewModelKeys, DataGridColumn<NegativeCarryoverReportViewModel>> = {
        id,
        agentUid,
        debtAfterPayment,
        debtBeforePayment,
        debtTransaction,
        totalNetShare,
        weekStartDateTs,
        weeklyActuallyPaid,
    };

    const columns = React.useMemo(() => {
        return keys.map(k => config[k]);
    }, [keys.join(',')]);
    return columns;
}

type UseWeeklySummaryExportDownloadProps = {
    keys: NegativeCarryoverWeeklySummaryViewModelKeys[];
    filterString: string;
    filename: MessageDescriptor;
    filter: KeyValueFilter[];
    mode: 'client' | 'server';
};

export function useNegativeCarryoverWeeklySummaryDownload({filter, filterString, ...props}: UseWeeklySummaryExportDownloadProps) {
    const {handleDownload, isProgress} = useDownload<NegativeCarryoverWeeklySummaryViewModelKeys>({
        ...props,
        strategySymbol:
            props.mode === 'client'
                ? NegativeCarryoverReportServiceTypes.WeeklySummaryDownloadClientStrategy
                : NegativeCarryoverReportServiceTypes.WeeklySummaryDownloadServerStrategy,
        headersLocalization: localizedWeeklySummaryHeaders,
        total: maxPageSize,
        filter: filterString,
    });
    const {showMessage} = useMessage();

    function isFilterValid(): boolean {
        const dateFrom = filter?.find(f => f.key === 'week_start_date_ts.from');
        const dateTo = filter?.find(f => f.key === 'week_start_date_ts.to');
        return isRangeInSameWeek(dateFrom?.value, dateTo?.value);
    }

    function handleClick() {
        if (isFilterValid()) {
            handleDownload();
        } else {
            showMessage({mode: 'error', message: localized.weeklySummaryInvalidDateRangeError});
        }
    }

    return {isProgress, handleClick};
}
